import MeterStatusApi from '@/data/meters/MeterStatusApi.ts';
import YieldInsight from '@/domain/yield-insights/YieldInsight.ts';
import AggregatorDTO from '@/domain/yield-insights/AggregatorDTO.ts';
import _ from 'lodash';
import LackingYieldReason from '@/domain/yield-insights/LackingYieldReason.ts';
import YieldApi from '@/data/yields/YieldApi.ts';
import { i18n } from '@/i18n';
import type Plant from '@/domain/model/business/Plant.ts';
import { format } from 'date-fns';
import type Company from '@/domain/model/business/Company.ts';

export async function getYieldInsightsForMeters(
  company: Company,
  plant: Plant,
  meters,
  dateSelection,
): Promise<Map<number, YieldInsight[]>> {
  const msTypesPromise = new MeterStatusApi()
    .getAllTypes()
    .then((rsp) => rsp.data.data);

  const meterPromises = meters.map((meter) => {
    const dto = new AggregatorDTO(company, plant, meter, dateSelection);
    return Promise.all([
      _createYieldPromise(dto, 'actual-yield'),
      _createYieldPromise(dto, 'predicted-yield'),
      _createMStatusPromise(dto),
      meter,
    ]);
  });

  let allData = await Promise.all([msTypesPromise, meterPromises]);
  const msTypes = allData[0];
  const dataForMeterPromises = allData[1];
  let dataForMeters = await Promise.all(dataForMeterPromises);
  return new Map(
    dataForMeters.map((dataForMeter) => [
      dataForMeter[3].getId(),
      _mapToYieldInsights(
        _extractLackingYields(dataForMeter[0], dataForMeter[1]),
        _extractMostDescriptiveStatuses(dataForMeter[2], msTypes),
      ),
    ]),
  );
}

export function determineLackingYieldReasons(
  yieldInsights: any[],
  locale: string,
  summarized: boolean = true,
) {
  const lackingYieldByType = _getLackingYieldByType(yieldInsights);
  const unknownReasons = _getUnknownReasons(lackingYieldByType);

  if (summarized) {
    // Order of operation is important, since we're modifying the original array
    const mostLikelyReasons = _getMostLikelyReasons(lackingYieldByType, locale);
    const otherReasons = _getOtherReasons(lackingYieldByType);

    return _.concat(mostLikelyReasons, otherReasons ?? [], unknownReasons);
  }

  const mappedReasons = lackingYieldByType.map(
    (reason) =>
      new LackingYieldReason(
        reason.meterStatus.type_name,
        reason.lackingYieldSum,
        reason.meterStatus.type['display_name_short_' + locale],
        [reason.meterStatus.type_name],
      ),
  );
  return _.concat(unknownReasons, mappedReasons);
}

export function getLackingYieldReasonsByDate(
  yieldInsights,
  globalOrderedReasons,
) {
  return _(yieldInsights)
    .orderBy('date')
    .groupBy('date')
    .map((insightsOnDate, date) => {
      const reasons = _mapInsightsByDateToLackingYieldReasons(
        insightsOnDate,
        globalOrderedReasons,
      );

      return {
        date,
        reasons,
      };
    })
    .mapKeys('date')
    .mapValues((value) => value.reasons)
    .value();
}

function _getUnknownReasons(lackingYieldByType) {
  const unknownStatusTypes = ['yield_present'];
  return _(lackingYieldByType)
    .remove(
      (item) =>
        !item.meterStatus ||
        unknownStatusTypes.includes(item.meterStatus.type_name),
    )
    .reduce(
      (reason, current) => {
        reason.lackingYieldSum += current.lackingYieldSum;
        return reason;
      },
      new LackingYieldReason(
        'unknown',
        0,
        i18n.global.t('global.unknown'),
        unknownStatusTypes,
      ),
    );
}

interface LackingYieldByType {
  meterStatus: {
    type_name: string;
    type: Record<string, string>;
  };
  lackingYieldSum: number;
}

function _getMostLikelyReasons(
  lackingYieldByType: Array<LackingYieldByType>,
  locale: string,
) {
  let mostLikelyReasons = _.take(lackingYieldByType, 2);
  lackingYieldByType.splice(0, 2);

  return mostLikelyReasons.map(
    (reason) =>
      new LackingYieldReason(
        reason.meterStatus.type_name,
        reason.lackingYieldSum,
        reason.meterStatus.type['display_name_short_' + locale],
        [reason.meterStatus.type_name],
      ),
  );
}

function _getOtherReasons(
  lackingYieldByType: LackingYieldByType[],
): LackingYieldReason | null {
  let otherReasons = new LackingYieldReason(
    'other',
    0,
    i18n.global.t('global.other'),
    [],
  );
  if (lackingYieldByType.length > 0) {
    otherReasons = _(lackingYieldByType).reduce((reason, item) => {
      reason.lackingYieldSum += item.lackingYieldSum;
      reason.underlyingStatusTypes.push(item.meterStatus.type_name);
      return reason;
    }, otherReasons);
  }

  return otherReasons.lackingYieldSum === 0 ? null : otherReasons;
}

function _extractLackingYields(actualYields, predictedYields) {
  return actualYields.map((ay) => {
    const py = predictedYields.find((p) => p.time == ay.time);

    return {
      date: format(new Date(ay.time), 'yyyy-MM-dd'),
      lackingYield: (py.yield_in_wh - ay.yield_in_wh) / 1000,
      actualYield: ay.yield_in_wh / 1000,
      predictedYield: py.yield_in_wh / 1000,
    };
  });
}

function _createYieldPromise(aggregatorDTO: AggregatorDTO, yieldType: string) {
  return new YieldApi().meterYield(
    aggregatorDTO.company,
    aggregatorDTO.plant,
    aggregatorDTO.meter,
    aggregatorDTO.getStartDateString(),
    aggregatorDTO.getEndDateString(),
    aggregatorDTO.getPeriodType(),
    yieldType,
  );
}

async function _createMStatusPromise(
  aggregatorDTO: AggregatorDTO,
): Promise<any> {
  let rsp = await new MeterStatusApi().meterStatusesForMeter(
    aggregatorDTO.company.getId(),
    aggregatorDTO.plant.getId(),
    aggregatorDTO.meter.getId(),
    aggregatorDTO.getStartDateString(),
    aggregatorDTO.getEndDateString(),
  );
  return await rsp.data.data;
}

function _extractMostDescriptiveStatuses(mStatuses, msTypes) {
  return _(mStatuses)
    .groupBy('date')
    .map((statusesByDate) => {
      let mostDescriptiveStatus;

      if (statusesByDate.length === 1) {
        mostDescriptiveStatus = statusesByDate[0];
        mostDescriptiveStatus.type = msTypes.find(
          (type) => type.name === mostDescriptiveStatus.type_name,
        );
      } else {
        mostDescriptiveStatus = statusesByDate.reduce(
          (previousStatus, currentStatus) => {
            return _getMostDescriptiveStatus(
              msTypes,
              previousStatus,
              currentStatus,
            );
          },
        );
      }

      return {
        date: mostDescriptiveStatus.date,
        status: mostDescriptiveStatus,
      };
    })
    .value();
}

function _mapToYieldInsights(lackingYields, meterStatuses) {
  return _(lackingYields)
    .concat(meterStatuses)
    .groupBy('date')
    .map((dateGroup) => {
      const mergedProperties = _.merge({}, ...dateGroup);

      return new YieldInsight(
        mergedProperties.date,
        mergedProperties.lackingYield ?? 0,
        mergedProperties.actualYield,
        mergedProperties.predictedYield,
        mergedProperties.status,
      );
    })
    .value();
}

function _getMostDescriptiveStatus(statusTypes, existingStatus, currentStatus) {
  const typeNames = _.keyBy(statusTypes, (statusType) => statusType.name);
  const existingStatusType = typeNames[existingStatus.type_name];
  const currentStatusType = typeNames[currentStatus.type_name];

  if (currentStatusType.descriptiveness > existingStatusType.descriptiveness) {
    currentStatus.type = currentStatusType;
    return currentStatus;
  } else {
    existingStatus.type = existingStatusType;
    return existingStatus;
  }
}

function _getLackingYieldByType(yieldInsights) {
  return _(yieldInsights)
    .groupBy('meterStatus.type_name')
    .map((insightsByType) => {
      const lackingYieldSum = Math.round(
        _.sumBy(insightsByType, 'lackingYield'),
      );

      return {
        meterStatus: insightsByType[0].meterStatus,
        lackingYieldSum: lackingYieldSum,
      };
    })
    .orderBy('lackingYieldSum', 'desc')
    .value();
}

function _mapInsightsByDateToLackingYieldReasons(
  insightsOnDate,
  orderedReasons,
) {
  return _(insightsOnDate)
    .filter((insight) => insight.meterStatus)
    .groupBy('meterStatus.type_name')
    .map((insights, type) => {
      const existingReason = orderedReasons.find((reason) =>
        reason.underlyingStatusTypes.includes(type),
      );

      return new LackingYieldReason(
        existingReason.reason,
        _.sumBy(insights, 'lackingYield'),
        existingReason.displayName,
        existingReason.underlyingStatusTypes,
      );
    })
    .value();
}
